import { inject, Injectable } from '@angular/core';
import { map } from 'rxjs';
import { ConstructAPI } from 'src/app/API/constructAPI';
import { RestApiService } from 'src/app/API/restapi';
import { adaptBusiness, adaptLicense, Business, BusinessDTO, License, LicenseDTO, toApiBusiness, toApiLicense } from 'src/model/business';
import { adaptBusinessBranch, BranchWithLicense, BusinessBranch, BusinessBranchDTO, toApiBranchWithLicense, toApiBusinessBranch } from 'src/model/business/branch.model';

type BusinessOptions = {
  companyId?: number,
  ssmExpired?: boolean
}

type BranchOptions = {
  businessId?: number,
  companyId?: number
}

type LicenseOptions = {
  active?: boolean,
  expired?: boolean,
  invalidated?: boolean,
  companyId?: number
  businessId?: number
}

@Injectable({
  providedIn: 'root',
})
export class BusinessService {
  private api = inject(RestApiService);
  private constructApi = inject(ConstructAPI);

  getAllBusinesses(o?: BusinessOptions): Promise<Business[]> {
    return new Promise<Business[]>((resolve, reject) => {
      this.api.get<BusinessDTO[]>(this.constructApi.getAllBusinesses(o?.companyId, o?.ssmExpired)).pipe(
        map(data => data.map(d => adaptBusiness(d)))
      ).subscribe({
        next: response => resolve(response),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to get all business: ', err);
          reject(err);
        }
      })
    })
  }

  getAllBusinessBranches(o?: BranchOptions): Promise<BusinessBranch[]> {
    return new Promise<BusinessBranch[]>((resolve, reject) => {
      this.api.get<BusinessBranchDTO[]>(this.constructApi.getAllBusinessBranches(o?.businessId, o?.companyId)).pipe(
        map(data => data.map(d => adaptBusinessBranch(d)))
      ).subscribe({
        next: response => resolve(response),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to get all business branches: ', err);
          reject(err);
        }
      })
    })
  }

  getAllLicenses(o?: LicenseOptions): Promise<License[]> {
    return new Promise<License[]>((resolve, reject) => {
      this.api.get<LicenseDTO[]>(this.constructApi.getAllLicenses(o?.active, o?.expired, o?.invalidated)).pipe(
        map(data => data.map(d => adaptLicense(d)))
      ).subscribe({
        next: response => resolve(response),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to get all business licenses: ', err);
          reject(err);
        }
      })
    })
  }

  getBusiness(id: number): Promise<Business> {
    return new Promise<Business>((resolve, reject) => {
      this.api.get<BusinessDTO>(this.constructApi.getBusinessById(id)).pipe(
        map(data => adaptBusiness(data))
      ).subscribe({
        next: response => resolve(response),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to get business : ', err);
          reject(err);
        }
      })
    })
  }

  getLicense(id: number): Promise<License> {
    return new Promise<License>((resolve, reject) => {
      this.api.get<LicenseDTO>(this.constructApi.getLicenseById(id)).pipe(
        map(data => adaptLicense(data))
      ).subscribe({
        next: response => resolve(response),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to get business license: ', err);
          reject(err);
        }
      })
    })
  }

  getLatestLicenseByCompanyId(companyId: number): Promise<License> {
    return new Promise<License>((resolve, reject) => {
      this.api.get<LicenseDTO>(this.constructApi.getLatestLicenseByCompanyId(companyId)).pipe(
        map(data => adaptLicense(data))
      ).subscribe({
        next: response => resolve(response),
        error: err => {
          if (err.status !== 404) {
            this.api.handleError(err);
            console.error('Failed to get company latest business license: ', err);
          }
          reject(err);
        }
      })
    })
  }

  getLatestLicenseByCustomerId(customerId: number): Promise<License> {
    return new Promise<License>((resolve, reject) => {
      this.api.get<LicenseDTO>(this.constructApi.getLatestLicenseByCustomerId(customerId)).pipe(
        map(data => adaptLicense(data))
      ).subscribe({
        next: response => resolve(response),
        error: err => {
          if (err.status !== 404) {
            this.api.handleError(err);
            console.error('Failed to get customer latest business license: ', err);
          }
          reject(err);
        }
      })
    })
  }

  postAddBusiness(business: Business): Promise<Business> {
    return new Promise<Business>((resolve, reject) => {
      this.api.post<BusinessDTO>(this.constructApi.getBusiness(), toApiBusiness(business)).subscribe({
        next: response => resolve(adaptBusiness(response)),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to add business: ', err);
          reject(err);
        }
      })
    })
  }

  putUpdateBusiness(business: Business): Promise<Business> {
    return new Promise<Business>((resolve, reject) => {
      this.api.put<BusinessDTO>(this.constructApi.getBusinessById(business.Id), toApiBusiness(business)).subscribe({
        next: response => resolve(adaptBusiness(response)),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to update business: ', err);
          reject(err);
        }
      })
    })
  }

  postAddBusinessBranch(branch: BranchWithLicense): Promise<BusinessBranch> {
    return new Promise<BusinessBranch>((resolve, reject) => {
      this.api.post<BusinessBranchDTO>(this.constructApi.getBusinessBranchLicense(), toApiBranchWithLicense(branch)).subscribe({
        next: response => {
        console.log(response);
        resolve(adaptBusinessBranch(response))
        },
        error: err => {
          this.api.handleError(err);
          console.error('Failed to add business branch: ', err);
          reject(err);
        }
      })
    })
  }

  putUpdateBusinessBranch(branch: BusinessBranch): Promise<BusinessBranch> {
    return new Promise<BusinessBranch>((resolve, reject) => {
      this.api.put<BusinessBranchDTO>(this.constructApi.getBusinessBranchById(branch.Id), toApiBusinessBranch(branch)).subscribe({
        next: response => resolve(adaptBusinessBranch(response)),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to update business branch: ', err);
          reject(err);
        }
      })
    })
  }

  postAddBusinessLicense(license: License): Promise<License> {
    return new Promise<License>((resolve, reject) => {
      this.api.post<LicenseDTO>(this.constructApi.getLicense(), toApiLicense(license)).subscribe({
        next: response => resolve(adaptLicense(response)),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to add business license: ', err);
          reject(err);
        }
      })
    })
  }

  putUpdateBusinessLicense(license: License): Promise<License> {
    return new Promise<License>((resolve, reject) => {
      this.api.put<LicenseDTO>(this.constructApi.getLicenseById(license.Id), toApiLicense(license)).subscribe({
        next: response => resolve(adaptLicense(response)),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to update business license: ', err);
          reject(err);
        }
      })
    })
  }

  putInvalidateLicense(id: number): Promise<License> {
    return new Promise<License>((resolve, reject) => {
      this.api.put<LicenseDTO>(this.constructApi.getInvalidateLicense(id)).subscribe({
        next: response => resolve(adaptLicense(response)),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to invalidate license: ', err);
          reject(err);
        }
      })
    })
  }

  putRevalidateLicense(id: number): Promise<License> {
    return new Promise<License>((resolve, reject) => {
      this.api.put<LicenseDTO>(this.constructApi.getRevalidateLicense(id)).subscribe({
        next: response => resolve(adaptLicense(response)),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to revalidate license: ', err);
          reject(err);
        }
      })
    })
  }

  putToggleConsentForm(businessId: number): Promise<Business> {
    return new Promise<Business>((resolve, reject) => {
      this.api.put<BusinessDTO>(this.constructApi.getBusinessToggleRequireConsentForm(businessId)).subscribe({
        next: response => resolve(adaptBusiness(response)),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to toggle consent form: ', err);
          reject(err);
        }
      })
    })
  }
}
